import React, { useMemo, useState, forwardRef, useEffect } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';

import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  FilterFn,
  PaginationState,
  Row,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { GetUsersResponse } from '@hud/portal-dtos/webapp-users.dto';
import useAnalytics from '../../../hooks/useAnalytics';

export type MembersTableProps = {
  members: GetUsersResponse;
  loading: boolean;
};

const columnHelper = createColumnHelper<GetUsersResponse[0]>();

const MembersTable = forwardRef<HTMLDivElement, MembersTableProps>(
  ({ members, loading }, ref) => {
    const analytics = useAnalytics();
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([
      { id: 'name', desc: false },
    ]);
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    const globalFilterFunction: FilterFn<GetUsersResponse[0]> = (
      row: Row<GetUsersResponse[0]>,
      _columnId: string,
      searchTerms: string,
    ) => {
      // split the search terms by space and check if all of them are included in the row
      return searchTerms
        .toLowerCase()
        .split(' ')
        .every((term) => {
          return Object.values(row.original).some((value) => {
            const cellValue = value?.toString().toLowerCase() || '';
            return cellValue.includes(term);
          });
        });
    };

    const columns = useMemo(() => {
      return [
        columnHelper.accessor('name', {
          header: 'Full Name',
          minSize: 50,
          size: 80,
        }),
        columnHelper.accessor('email', {
          header: 'Email',
          minSize: 50,
        }),
        columnHelper.accessor('role', {
          header: 'Role',
          minSize: 30,
          // capitalize the first letter of the role
          cell: (cell) => {
            const role = cell.getValue();
            return role.charAt(0).toUpperCase() + role.slice(1);
          },
          sortingFn: (a, b) => {
            const aRole = a.original.role;
            const bRole = b.original.role;
            // admin should come first
            if (aRole === 'admin') return -1;
            if (bRole === 'admin') return 1;
            return aRole.localeCompare(bRole);
          },
        }),
      ];
    }, []);

    const table = useReactTable({
      data: members ?? [],
      columns: columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      columnResizeMode: 'onChange',
      globalFilterFn: globalFilterFunction,
      enableSortingRemoval: false,

      state: {
        pagination,
        sorting,
        globalFilter,
      },
      defaultColumn: {
        minSize: 80,
      },
      onSortingChange: setSorting,
    });

    useEffect(() => {
      if (globalFilter.length === 0) return;
      analytics.capture('users_searched', {
        search: globalFilter,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalFilter]);

    return (
      <TableContainer.Container ref={ref} id="members">
        <TableContainer.Header>
          <Search
            inline
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          {loading ? (
            <Skeleton width={100} />
          ) : members ? (
            <ItemCounter
              total={members.length}
              filtered={table.getPrePaginationRowModel().rows.length}
            />
          ) : null}
        </TableContainer.Header>
        <HorizontalContainer>
          <DefaultTable
            table={table}
            label="Members Table"
            loading={loading}
            loadingRows={5}
          />
        </HorizontalContainer>
        <TableContainer.Footer>
          <Pagination
            pages={loading ? 0 : table.getPageCount()}
            currentPage={
              loading || table.getPageCount() === 0
                ? 0
                : table.getState().pagination.pageIndex + 1
            }
            onPageChange={(page) => table.setPageIndex(page - 1)}
            loading={loading}
          />
        </TableContainer.Footer>
      </TableContainer.Container>
    );
  },
);

export default MembersTable;
